'use client'

import { useQuery } from '@tanstack/react-query'

import { useClubPublicProfile } from '@/features/clubs/hooks'
import { normalizeLeaderboardSportCode } from '@/features/leaderboard/buildLeaderboardSportTabs'

import { fetchClubsLeaderboardPosition } from './api'

/**
 * The club's rank on the World board for the selected sport. Before the
 * gateway serves /leaderboards/clubs/position, the club profile ranks stand
 * in: `global_rank` for All Sports, `club_rank` for the club's own sport.
 */
export function useClubsLeaderboardRank(
  clubId: string | undefined,
  sportType: string | undefined
): number | undefined {
  const position = useQuery({
    queryKey: ['clubs_leaderboard_position', clubId, sportType ?? 'all'],
    queryFn: () => fetchClubsLeaderboardPosition(clubId ?? '', sportType),
    enabled: Boolean(clubId),
    staleTime: 30_000,
    retry: false
  })

  const fallbackToProfile = position.data === null
  const { data: profile } = useClubPublicProfile(
    fallbackToProfile ? clubId : undefined
  )

  if (position.data) {
    return position.data.rank
  }
  if (!fallbackToProfile || !profile) {
    return undefined
  }
  if (!sportType) {
    return profile.global_rank
  }
  const clubSport = normalizeLeaderboardSportCode(
    profile.club?.sport_type ?? ''
  )
  return clubSport === sportType ? profile.club_rank : undefined
}
